import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { memo, useEffect, useRef } from 'react';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import NotificationCard from './NotificationCard';
import { useStore } from 'zustand';
import notificationsSlice from '../../store/notificationsSlice';
import { useNotificationsAllQuery } from 'src/app/queries/notifications.queries';
import {
  deleteAllNotifications,
  deleteByIdNotifications,
  readAllNotifications,
} from 'src/app/api/NotificationsApi';
import { useQueryClient } from '@tanstack/react-query';
import IQueryKeys from 'src/app/types/IQueryKeys';

const StyledSwipeableDrawer = styled(SwipeableDrawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    backgroundColor: theme.palette.background.default,
    width: 320,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
}));

function NotificationPanel(props) {
  const state = useStore(notificationsSlice);
  const { data: notificationsResult } = useNotificationsAllQuery();
  const notifications = notificationsResult?.data.items || [];
  const queryClient = useQueryClient();
  const hasUnread = notifications.some((n) => !n.read);
  const { enqueueSnackbar } = useSnackbar();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (state.open && hasUnread) {
      readAllNotifications().then(() => {
        queryClient.refetchQueries({ queryKey: [IQueryKeys.notificationAllKey] });
      });
    }
  }, [state.open]);

  async function handleDismiss(id) {
    try {
      await deleteByIdNotifications(id);
      queryClient.refetchQueries({ queryKey: [IQueryKeys.notificationAllKey] });
    } catch {
      enqueueSnackbar('Sorry, something went wrong.', { variant: 'errorToast' });
    }
  }

  async function handleDismissAll() {
    try {
      await deleteAllNotifications();
      queryClient.refetchQueries({ queryKey: [IQueryKeys.notificationAllKey] });
    } catch {
      enqueueSnackbar('Sorry, something went wrong.', { variant: 'errorToast' });
    }
  }

  return (
    <StyledSwipeableDrawer
      open={state.open}
      anchor="right"
      onOpen={() => {}}
      onClose={() => state.toggleNotificationPanel()}
      disableSwipeToOpen
    >
      <div className="p-14 flex justify-between items-center flex-shrink-0">
        <Typography className="text-28 font-semibold leading-none">Notifications</Typography>
        <IconButton
          className="absolute top-0 right-0 z-999"
          onClick={() => state.closeNotificationPanel()}
          size="large"
        >
          <FuseSvgIcon color="action">heroicons-outline:x</FuseSvgIcon>
        </IconButton>
      </div>

      {notifications.length > 0 ? (
        <>
          <div className="flex justify-end px-16 py-4 flex-shrink-0">
            <Typography
              className="text-12 underline cursor-pointer"
              color="secondary"
              onClick={handleDismissAll}
            >
              dismiss all
            </Typography>
          </div>

          <div className="flex-1 overflow-auto px-16 pb-16" ref={scrollRef}>
            {notifications.map((notification) => (
              <NotificationCard
                key={notification.id}
                className="mb-16"
                notification={notification}
                onClose={handleDismiss}
              />
            ))}
            <div className="h-8" />
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center p-16">
          <Typography className="text-24 text-center" color="text.secondary">
            There are no notifications for now.
          </Typography>
        </div>
      )}
    </StyledSwipeableDrawer>
  );
}

export default memo(NotificationPanel);
